import { useCallback, useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'
import {
  lumaDifference,
  meanLumaRgba,
  TEST_CHANNEL_BLACK_LUMA,
  TEST_CHANNEL_SILENCE_DBFS,
  samplesToDbfs,
  type TestChannelPlayerPhase,
} from '../lib/testChannelSignal.js'

/**
 * Lecteur de la réplique 1080p50 du canal de test (CH2), servie en HLS par la
 * passerelle média derrière `/stream/hls/ch2/`.
 *
 * Lecteur à nous (hls.js) plutôt que la page de la passerelle en iframe : il
 * faut lire le son pour les vu-mètres et l'image pour détecter le figé — ce
 * qu'une page d'une autre application ne laisse pas faire. Même origine que
 * l'application, donc l'analyse audio (WebAudio) et la lecture des pixels
 * (canvas) sont permises.
 */
export const TEST_CHANNEL_HLS_URL = '/stream/hls/ch2/index.m3u8'

const RETRY_DELAY_MS = 8_000
const STALL_TIMEOUT_MS = 12_000
const SAMPLE_INTERVAL_MS = 500
const MOTION_THRESHOLD = 0.6

export interface TestChannelMeasures {
  width:      number | null
  height:     number | null
  fps:        number | null
  bitrate:    number | null
  latencySec: number | null
  audioCodec: string | null
  silentForMs: number
  frozenForMs: number
  black:      boolean
}

export interface TestChannelAudioTaps {
  left:  AnalyserNode | null
  right: AnalyserNode | null
}

export interface TestChannelStream {
  videoRef:  React.MutableRefObject<HTMLVideoElement | null>
  phase:     TestChannelPlayerPhase
  measures:  TestChannelMeasures
  /** Analyseurs G/D lus par les vu-mètres à chaque image, sans rendu React. */
  audioRef:  React.MutableRefObject<TestChannelAudioTaps>
  muted:     boolean
  volume:    number
  /** Le navigateur a bloqué le démarrage avec le son : un clic le débloque. */
  needsGesture: boolean
  setMuted:  (muted: boolean) => void
  setVolume: (volume: number) => void
  unlock:    () => void
  reload:    () => void
}

const EMPTY_MEASURES: TestChannelMeasures = {
  width: null, height: null, fps: null, bitrate: null, latencySec: null, audioCodec: null,
  // Noir supposé tant qu’aucune image n’est lue : pas de faux « hors TrafficPlay ».
  silentForMs: 0, frozenForMs: 0, black: true,
}

export function useTestChannelStream(options: { initiallyMuted?: boolean } = {}): TestChannelStream {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const audioRef = useRef<TestChannelAudioTaps>({ left: null, right: null })
  const gainRef = useRef<GainNode | null>(null)
  const contextRef = useRef<AudioContext | null>(null)
  const [phase, setPhase] = useState<TestChannelPlayerPhase>('LOADING')
  const [measures, setMeasures] = useState<TestChannelMeasures>(EMPTY_MEASURES)
  const [muted, setMutedState] = useState(options.initiallyMuted ?? false)
  const [volume, setVolumeState] = useState(0.8)
  const [needsGesture, setNeedsGesture] = useState(false)
  const [generation, setGeneration] = useState(0)
  const mutedRef = useRef(muted)
  const volumeRef = useRef(volume)

  const applyGain = useCallback(() => {
    const gain = gainRef.current
    if (gain) gain.gain.value = mutedRef.current ? 0 : volumeRef.current
  }, [])

  const setMuted = useCallback((next: boolean) => {
    mutedRef.current = next
    setMutedState(next)
    applyGain()
    void contextRef.current?.resume().catch(() => {})
  }, [applyGain])

  const setVolume = useCallback((next: number) => {
    volumeRef.current = next
    setVolumeState(next)
    applyGain()
  }, [applyGain])

  const reload = useCallback(() => setGeneration(value => value + 1), [])

  const unlock = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = false
    void contextRef.current?.resume().catch(() => {})
    void video.play().then(() => setNeedsGesture(false)).catch(() => {})
  }, [])

  // ── Lecture HLS ──────────────────────────────────────────────────────────
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let disposed = false
    let hls: Hls | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let bytesInWindow = 0
    let windowStartedAt = performance.now()
    let bitrate: number | null = null
    let audioCodec: string | null = null
    setPhase('LOADING')
    setMeasures(EMPTY_MEASURES)

    const scheduleRetry = () => {
      if (disposed || retryTimer) return
      setPhase('UNAVAILABLE')
      retryTimer = setTimeout(() => { retryTimer = null; if (!disposed) setGeneration(value => value + 1) }, RETRY_DELAY_MS)
    }

    const startPlayback = () => {
      // Le son passe par WebAudio (gain + vu-mètres) : l'élément reste non
      // muet pour alimenter l'analyse, le gain décide de ce qu'on entend.
      video.muted = false
      video.volume = 1
      video.play().then(() => setNeedsGesture(false)).catch(() => {
        // Démarrage avec le son refusé par le navigateur : on démarre l'image
        // en muet et on propose un clic, qui débloquera son et vu-mètres.
        video.muted = true
        setNeedsGesture(true)
        void video.play().catch(() => {})
      })
    }

    const attachAudio = () => {
      if (contextRef.current) return
      try {
        const context = new AudioContext()
        const source = context.createMediaElementSource(video)
        const splitter = context.createChannelSplitter(2)
        const left = context.createAnalyser()
        const right = context.createAnalyser()
        left.fftSize = 1024
        right.fftSize = 1024
        const gain = context.createGain()
        source.connect(splitter)
        splitter.connect(left, 0)
        splitter.connect(right, 1)
        source.connect(gain)
        gain.connect(context.destination)
        contextRef.current = context
        gainRef.current = gain
        audioRef.current = { left, right }
        applyGain()
      } catch {
        // WebAudio indisponible : l'image reste, sans vu-mètres.
      }
    }

    const onPlaying = () => { if (!disposed) setPhase('PLAYING') }
    video.addEventListener('playing', onPlaying)

    void (async () => {
      const { default: HlsClass } = await import('hls.js')
      if (disposed) return
      attachAudio()
      if (HlsClass.isSupported()) {
        hls = new HlsClass({
          lowLatencyMode:   true,
          backBufferLength: 10,
          maxBufferLength:  6,
          enableWorker:     true,
        })
        hls.on(HlsClass.Events.FRAG_LOADED, (_event, data) => {
          bytesInWindow += data.payload?.byteLength ?? 0
          const elapsed = performance.now() - windowStartedAt
          if (elapsed >= 4_000) {
            bitrate = (bytesInWindow * 8 * 1000) / elapsed
            bytesInWindow = 0
            windowStartedAt = performance.now()
          }
        })
        hls.on(HlsClass.Events.MANIFEST_PARSED, (_event, data) => {
          const codec = data.levels[0]?.audioCodec ?? ''
          audioCodec = codec.startsWith('mp4a') ? 'AAC' : codec.startsWith('opus') ? 'Opus' : codec || null
          startPlayback()
        })
        hls.on(HlsClass.Events.ERROR, (_event, data) => {
          if (!data.fatal) return
          if (data.type === HlsClass.ErrorTypes.MEDIA_ERROR) { hls?.recoverMediaError(); return }
          hls?.destroy()
          hls = null
          scheduleRetry()
        })
        hls.loadSource(TEST_CHANNEL_HLS_URL)
        hls.attachMedia(video)
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari : HLS natif.
        video.src = TEST_CHANNEL_HLS_URL
        video.addEventListener('error', scheduleRetry, { once: true })
        startPlayback()
      } else {
        scheduleRetry()
      }
    })()

    // ── Mesures : cadence, mouvement, noir, silence ────────────────────────
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 36
    const context2d = canvas.getContext('2d', { willReadFrequently: true })
    let previousGray: Uint8ClampedArray | null = null
    let lastMotionAt = performance.now()
    let lastSoundAt = performance.now()
    let lastFrames = 0
    let lastFramesAt = performance.now()
    let lastTime = -1
    let lastProgressAt = performance.now()
    const samples = new Float32Array(1024)

    const sample = () => {
      if (disposed) return
      const now = performance.now()

      // Chien de garde : la vidéo ne progresse plus → on relance le lecteur.
      if (video.currentTime !== lastTime) { lastTime = video.currentTime; lastProgressAt = now }
      else if (!document.hidden && now - lastProgressAt > STALL_TIMEOUT_MS && video.readyState > 0) {
        setGeneration(value => value + 1)
        return
      }

      let black = false
      if (context2d && video.readyState >= 2) {
        try {
          context2d.drawImage(video, 0, 0, canvas.width, canvas.height)
          const rgba = context2d.getImageData(0, 0, canvas.width, canvas.height).data
          const gray = new Uint8ClampedArray(rgba.length / 4)
          for (let pixel = 0; pixel < gray.length; pixel += 1) {
            gray[pixel] = 0.2126 * rgba[pixel * 4] + 0.7152 * rgba[pixel * 4 + 1] + 0.0722 * rgba[pixel * 4 + 2]
          }
          black = meanLumaRgba(rgba) < TEST_CHANNEL_BLACK_LUMA
          const difference = previousGray ? lumaDifference(previousGray, gray) : null
          if (difference === null || difference > MOTION_THRESHOLD) lastMotionAt = now
          previousGray = gray
        } catch {
          lastMotionAt = now
        }
      }

      const { left, right } = audioRef.current
      let peak = -90
      for (const analyser of [left, right]) {
        if (!analyser) continue
        analyser.getFloatTimeDomainData(samples)
        peak = Math.max(peak, samplesToDbfs(samples))
      }
      // Sans analyse audio (navigateur bloqué), on ne prétend pas au silence.
      if (peak > TEST_CHANNEL_SILENCE_DBFS || !left || video.muted) lastSoundAt = now

      const quality = video.getVideoPlaybackQuality?.()
      let fps: number | null = null
      if (quality) {
        const frames = quality.totalVideoFrames - quality.droppedVideoFrames
        const elapsed = (now - lastFramesAt) / 1000
        if (elapsed >= 1 && lastFrames) fps = Math.round((frames - lastFrames) / elapsed)
        if (elapsed >= 1) { lastFrames = frames; lastFramesAt = now }
      }

      setMeasures(previous => ({
        width:       video.videoWidth || null,
        height:      video.videoHeight || null,
        fps:         fps ?? previous.fps,
        bitrate,
        latencySec:  hls?.latency && Number.isFinite(hls.latency) ? hls.latency : null,
        audioCodec,
        silentForMs: now - lastSoundAt,
        frozenForMs: now - lastMotionAt,
        black,
      }))
    }
    const sampler = setInterval(sample, SAMPLE_INTERVAL_MS)

    return () => {
      disposed = true
      clearInterval(sampler)
      if (retryTimer) clearTimeout(retryTimer)
      video.removeEventListener('playing', onPlaying)
      hls?.destroy()
      video.removeAttribute('src')
      video.load()
    }
  }, [generation, applyGain])

  // Contexte audio : un seul par élément vidéo, fermé au démontage.
  useEffect(() => () => {
    void contextRef.current?.close().catch(() => {})
    contextRef.current = null
    gainRef.current = null
    audioRef.current = { left: null, right: null }
  }, [])

  return { videoRef, phase, measures, audioRef, muted, volume, needsGesture, setMuted, setVolume, unlock, reload }
}
